import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Badge, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrashAlt,
  faArrowLeft,
  faInfoCircle,
  faHashtag,
  faCalendarAlt,
  faUser,
  faServer
} from '@fortawesome/free-solid-svg-icons';
import { getMeasurementUnitDetails, deleteMeasurementUnit } from '../../Controller/MeasurementUnitController';
import ConfirmationAlert from '../../../../../GlobalModules/Utility/Components/ConfirmationAlert';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import '../../Assets/css/measurement-units.scss';

/**
 * Component for viewing measurement unit details
 * @returns {JSX.Element}
 */
const MeasurementUnitView = (props) => {
  const { t } = props;
  const { lng, id } = useParams();
  const history = useHistory();

  const [measurementUnit, setMeasurementUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch measurement unit details on component mount
  useEffect(() => {
    if (id) {
      fetchMeasurementUnitDetails();
    }
  }, [id]);

  /**
   * Fetch measurement unit details
   */
  const fetchMeasurementUnitDetails = async () => {
    setLoading(true);
    try {
      const response = await getMeasurementUnitDetails(id);

      if (response.success) {
        setMeasurementUnit(response.data);
      } else {
        Utility.toastNotifications(response.message || 'Failed to fetch measurement unit details', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error fetching measurement unit details:', error);
      Utility.toastNotifications('An error occurred while fetching measurement unit details', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open delete confirmation modal
   */
  const confirmDelete = () => {
    setShowDeleteModal(true);
  };

  /**
   * Close delete confirmation modal
   */
  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  /**
   * Handle delete action
   */
  const handleDelete = async () => {
    try {
      setLoading(true);

      const response = await deleteMeasurementUnit(id);

      if (response.success) {
        Utility.toastNotifications(response.message || 'Measurement unit deleted successfully', 'Success', 'success');
        // Navigate back to list page
        history.push(`/${lng}/settings/measurement-units`);
      } else {
        Utility.toastNotifications(response.message || 'Failed to delete measurement unit', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error deleting measurement unit:', error);
      Utility.toastNotifications('An error occurred while deleting the measurement unit', 'Error', 'error');
    } finally {
      setShowDeleteModal(false);
      setLoading(false);
    }
  };

  /**
   * Handle back button click
   */
  const handleBack = () => {
    history.push(`/${lng}/settings/measurement-units`);
  };

  //======= add action permission ====//
  // Check multiple permissions at once
  const measurementUnitViewPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'measurement_units',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  if (loading) {
    return (
      <Container fluid className='p-0'>
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '400px' }}
        >
          <Spinner animation='border' variant='primary' />
          <span className='ms-2'>Loading measurement unit details...</span>
        </div>
      </Container>
    );
  }

  if (!measurementUnit) {
    return (
      <Container fluid className='p-0'>
        <div className='text-center py-5'>
          <p>Measurement unit not found or has been deleted.</p>
          <Button variant='primary' onClick={handleBack}>
            Back to List
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className='p-0 measurement-unit-view-page'>
      <Row className='top-header-box measurement-unit-view-header mb-3'>
        <Col className='measurement-unit-header-content'>
          <div className='measurement-unit-title-row'>
            <h4 className='measurement-unit-title'>
              {measurementUnit.label || 'Measurement Unit'}
            </h4>
            <Badge
              bg={measurementUnit.status === 1 ? 'success' : 'danger'}
              className='badgeCustom'
            >
              {measurementUnit.status === 1 ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </Col>
        <Col xs='auto' className='measurement-unit-header-actions'>
          {measurementUnitViewPermissions.edit && measurementUnit?.is_system_locked !== 1 && (
            <Button
              variant='outline-primary'
              onClick={() => history.push(`/${lng}/settings/measurement-units/edit/${id}`)}
            >
              <FontAwesomeIcon icon={faEdit} className='me-1' />
              Edit
            </Button>
          )}
          {measurementUnitViewPermissions.delete && measurementUnit?.is_system_locked !== 1 && (
            <Button variant='danger' onClick={confirmDelete}>
              <FontAwesomeIcon icon={faTrashAlt} className='me-1' />
              Delete
            </Button>
          )}
          <Button
            variant='outline-secondary'
            onClick={handleBack}
          >
            <FontAwesomeIcon icon={faArrowLeft} className='me-1' />
            Back to List
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className='custom-border-light mb-4 main-content-card measurement-unit-details-card'>
            <Card.Header>
              <h6>
                <FontAwesomeIcon icon={faInfoCircle} className='me-2' />
                Basic Information
              </h6>
            </Card.Header>
            <Card.Body>
              <div className='measurement-unit-details-grid'>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Label</span>
                    <span className='detail-value'>
                      {measurementUnit.label || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faHashtag} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Unit Code</span>
                    <span className='detail-value'>
                      {measurementUnit.unit_code || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Status</span>
                    <span className='detail-value'>
                      <Badge
                        bg={measurementUnit.status === 1 ? 'success' : 'danger'}
                        className='badgeCustom'
                      >
                        {measurementUnit.status === 1 ? 'Active' : 'Inactive'}
                      </Badge>
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className='custom-border-light mb-4 main-content-card'>
            <Card.Header>
              <h6>
                <FontAwesomeIcon icon={faServer} className='me-2' />
                System Information
              </h6>
            </Card.Header>
            <Card.Body>
              <div className='measurement-unit-details-grid'>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Created On</span>
                    <span className='detail-value'>
                      {Utility.get12HourFormat(measurementUnit.createdAt) || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Last Updated</span>
                    <span className='detail-value'>
                      {Utility.get12HourFormat(measurementUnit.updatedAt) || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Created By</span>
                    <span className='detail-value'>
                      {measurementUnit?.created_by_user || measurementUnit?.CreatedUser ? measurementUnit?.CreatedUser?.first_name+ " "+measurementUnit?.CreatedUser?.last_name || 'N/A' : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ConfirmationAlert
            BodyFirstContent="Are you sure you want to delete measurement unit:"
            BodySecondContent={measurementUnit?.name}
            BodyThirdContent="?"
            BodyForthContent="This action cannot be undone."
            confirmationButtonContent="Yes, delete it!"
            cancelButtonContent="Cancel"
            deleteIconVissable={true}
            deleteConfirmButton={handleDelete}
            deleteCancleButton={cancelDelete}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token
  };
}

export default connect(mapStateToProps)(withNamespaces()(MeasurementUnitView));

